import * as Icons from "lucide-react";
import { BedDouble } from "lucide-react";
import { SLEEPING, IMAGES, PROPERTY, SUITE_PERKS } from "@/data/property";
import { Reveal, MaskLineInView } from "./Reveal";

export default function Sleeping() {
  return (
    <section id="sleeping" data-testid="sleeping-section" className="bg-[#FAFAF8] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <span className="text-sm uppercase tracking-widest text-[#1FB6C9] font-body">Sleeping Arrangements</span>
          <h2 className="mt-3 font-display font-semibold text-[#0E2A47] text-4xl md:text-6xl tracking-tight leading-[0.95]">
            <MaskLineInView>Room for</MaskLineInView>
            <MaskLineInView delay={0.1}>
              <span className="font-serif-accent italic font-normal text-[#1FB6C9]">everyone.</span>
            </MaskLineInView>
          </h2>
          <p className="mt-6 text-[#0E2A47]/70 font-body text-lg max-w-md">
            8 bedrooms · 18 beds · 9 full bathrooms. Every private bedroom is a true suite with its own attached full bathroom — no shared baths.
          </p>

          {/* Signature callout: what's in every bedroom */}
          <div data-testid="suite-perks-card" className="mt-8 rounded-2xl border border-[#1FB6C9]/25 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-[#1FB6C9] mb-4">
              <Icons.Sparkles size={16} />
              <span className="text-xs uppercase tracking-widest font-body">In every private bedroom</span>
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
              {SUITE_PERKS.map((p) => {
                const Icon = Icons[p.icon] || Icons.Check;
                return (
                  <li key={p.label} className="flex items-center gap-3 text-[#0E2A47] font-body text-sm">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F1E8DA] text-[#0E2A47]">
                      <Icon size={15} strokeWidth={1.8} />
                    </span>
                    {p.label}
                  </li>
                );
              })}
            </ul>
            <p className="mt-5 text-xs text-[#0E2A47]/50 font-body">
              Bunk rooms are dedicated bunk spaces and don't include individual climate control, TV, or ceiling fan.
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-lg">
            <img
              src={IMAGES.greatRoom}
              alt="Airy great room with vaulted wood ceilings and lake-view windows"
              className="w-full h-72 object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
          <div className="mt-8 flex gap-8">
            {PROPERTY.stats.slice(1).map((s) => (
              <div key={s.label}>
                <div className="font-display font-semibold text-3xl text-[#0E2A47]">{s.value}</div>
                <div className="text-xs uppercase tracking-widest text-[#0E2A47]/50">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {SLEEPING.map((r, i) => (
            <Reveal key={r.room} delay={(i % 4) * 0.05}>
              <div
                data-testid={`sleeping-${i}`}
                className="group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 bg-white rounded-lg p-5 border border-[#0E2A47]/5 transition-all duration-300 hover:border-[#1FB6C9]/40 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F1E8DA] text-[#0E2A47] transition-colors duration-300 group-hover:bg-[#1FB6C9] group-hover:text-white">
                  <BedDouble size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-medium text-[#0E2A47]">{r.room}</span>
                    {r.suite && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#1FB6C9]/10 text-[#0E2A47] text-[10px] uppercase tracking-widest font-body px-2 py-0.5">
                        <Icons.Sparkles size={10} className="text-[#1FB6C9]" /> Suite
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[#0E2A47]/60 font-body">{r.note}</div>
                  {r.suite && (
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#0E2A47]/60 font-body">
                      <span className="inline-flex items-center gap-1"><Icons.Bath size={12} className="text-[#1FB6C9]" /> Private bath</span>
                      <span className="inline-flex items-center gap-1"><Icons.Thermometer size={12} className="text-[#1FB6C9]" /> Climate control</span>
                      <span className="inline-flex items-center gap-1"><Icons.Tv size={12} className="text-[#1FB6C9]" /> Smart TV</span>
                      <span className="inline-flex items-center gap-1"><Icons.Fan size={12} className="text-[#1FB6C9]" /> Ceiling fan</span>
                    </div>
                  )}
                </div>
                <div className="text-right font-display text-sm font-medium text-[#1FB6C9] sm:self-center">{r.beds}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
